import { tgpu } from 'typegpu'
import { vec2f } from 'typegpu/data'
import { structToSchema } from '@/utils/schemaUtil'
import * as v from '@/valibot'
import { VariationInfo } from '../simple/types'
import type { TgpuFn } from 'typegpu'
import type { BaseData, Infer, InferGPU, v2f, Vec2f, WgslStruct, } from 'typegpu/data'
import type { VariationCategory } from '../categories'
import type { EditorFor } from '@/components/Sliders/ParametricEditors/types'
import type { BooleanSchema, OptionalSchema } from '@/valibot'

type ParametricVariationDescriptor<
  K extends string,
  T extends Record<string, BaseData>,
> = v.ObjectSchema<
  {
    readonly type: v.LiteralSchema<K, undefined>
    readonly weight: v.NumberSchema<undefined>
    readonly visible: OptionalSchema<BooleanSchema<undefined>, true>
    readonly params: v.ObjectSchema<
      Record<keyof T, v.NumberSchema<undefined>>,
      undefined
    >
  },
  undefined
>

type ParametricVariation<
  K extends string,
  T extends Record<string, BaseData>,
> = {
  category: VariationCategory
  DescriptorSchema: ParametricVariationDescriptor<K, T>
  paramStruct: WgslStruct<T>
  paramDefaults: Infer<WgslStruct<T>>
  editor: EditorFor<Infer<WgslStruct<T>>>
  fn: TgpuFn<
    (
      pos: Vec2f,
      variationInfo: typeof VariationInfo,
      params: WgslStruct<T>,
    ) => Vec2f
  >
}

export function parametricVariationDescriptor<
  K extends string,
  T extends Record<string, BaseData>,
>(
  variationType: K,
  paramStruct: WgslStruct<T>,
  paramDefaults?: Infer<WgslStruct<T>>,
): ParametricVariationDescriptor<K, T> {
  const ParamSchema = structToSchema(paramStruct)
  // We use type assertion here to bypass strict return type while still emitting the correct type signature
  return v.object({
    type: v.literal(variationType),
    weight: v.number(),
    visible: v.optional(v.boolean(), true),
    params: (paramDefaults
      ? v.optional(
          ParamSchema,
          () =>
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            paramDefaults as any,
        )
      : ParamSchema) as unknown as v.ObjectSchema<
      Record<keyof T, v.NumberSchema<undefined>>,
      undefined
    >,
  })
}

export function parametricVariation<
  K extends string,
  T extends Record<string, BaseData>,
>(
  variationKey: K,
  paramStruct: WgslStruct<T>,
  paramDefaults: Infer<WgslStruct<T>>,
  editor: EditorFor<Infer<WgslStruct<T>>>,
  impl: (
    pos: v2f,
    varInfo: VariationInfo,
    params: InferGPU<WgslStruct<T>>,
  ) => v2f,
  category: VariationCategory = 'general',
): ParametricVariation<K, T> {
  return {
    category,
    DescriptorSchema: parametricVariationDescriptor(
      variationKey,
      paramStruct,
      paramDefaults,
    ),
    paramStruct,
    paramDefaults,
    editor,
    fn: tgpu
      .fn([vec2f, VariationInfo, paramStruct], vec2f)(impl)
      .$name(variationKey),
  }
}
